import { useEffect, useMemo, useState } from 'react';
import { Box } from '@nimbus-ds/components';
import { Page } from '@nimbus-ds/patterns';
import { useLocation } from 'react-router';
import { useNavegate } from 'App/hooks';
import { HeaderTop, IonPageStratus } from 'commons/components';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import { DocumentType } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/constants';
import { useGetInformationForDocument } from 'domains/FulfillmentOrders/pages/PrintDocumentPage/hooks';
import useCreateAsyncNuvemEnvioLabels from 'domains/NuvemEnvioCommons/Orders/hooks/useCreateAsyncNuvemEnvioLabels/useCreateAsyncNuvemEnvioLabels';
import useDownloadNuvemEnvioLabel from 'domains/NuvemEnvioCommons/Orders/hooks/useDownloadNuvemEnvioLabel/useDownloadNuvemEnvioLabel';
import { usePolling } from 'domains/NuvemEnvioCommons/Orders/hooks/usePolling/usePolling';
import useSearchNuvemEnvioLabels from 'domains/NuvemEnvioCommons/Orders/hooks/useSearchNuvemEnvioLabels/useSearchNuvemEnvioLabels';
import {
  getQueryParams,
  NE_POOLING_LABELS_SEARCH_PROCESS_INTERVAL,
  NE_POOLING_LABELS_SEARCH_PROCESS_TIMEOUT,
} from 'domains/NuvemEnvioCommons/Orders/utils';
import getReturnPrintLabelUrl from 'domains/NuvemEnvioCommons/Orders/utils/getReturnPrintLabelUrl';
import {
  trackingNuvemEnvioPrintDocumentsGeneratingStartLabels,
  trackingNuvemEnvioPrintDocumentsGeneratedPrintLabelsSuccess,
  trackingNuvemEnvioPrintDocumentsGeneratedErrorLabels,
} from 'domains/NuvemEnvioCommons/tracking';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import {
  PdfPage,
  PrintLabelHeader,
  PrintLabelResult,
  ProgressCard,
} from './components';
import OrdersSafariAlert from '../../components/SafariAlert';
import useNavigatorInfo from '../../hooks/useNavigatorInfo/useNavigatorInfo';
import { combineLabelErrorCodes } from '../../utils/async-errors';

function OrdersAsyncLabelGenerateStatusPage(): JSX.Element {
  const { goBack } = useNavegate();
  const { isSafari } = useNavigatorInfo();
  const { nuvemEnvioConfigs } = useGetNuvemEnvioConfigs();

  const {
    createNuvemEnvioAsyncLabels,
    resetNuvemEnvioAsyncLabels,
    data: createNuvemEnvioAsyncLabelsData,
    status: createNuvemEnvioAsyncLabelsStatus,
  } = useCreateAsyncNuvemEnvioLabels();
  const {
    searchNuvemEnvioLabels,
    resetSearchNuvemEnvioLabels,
    data: searchNuvemEnvioLabelsData,
    status: searchNuvemEnvioLabelsStatus,
  } = useSearchNuvemEnvioLabels();
  const { fetchInformationForDocument, information } =
    useGetInformationForDocument(DocumentType.NUVEM_ENVIO_LABEL);
  const {
    data: downloadNuvemEnvioLabelData,
    status: downloadNuvemEnvioLabelStatus,
    resetDownloadNuvemEnvioLabel,
  } = useDownloadNuvemEnvioLabel();
  const isMobile = useIsMobileDevice();

  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const { ids, source, orderId } = getQueryParams(searchParams);

  const [shouldStartPolling, setShouldStartPolling] = useState<boolean>(false);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);

  useEffect(() => {
    fetchInformationForDocument({ fulfillmentOrders: [ids[0]] });
    createNuvemEnvioAsyncLabels({
      fulfillmentOrderIds: [ids[0]],
      nuvemEnvioCarrierId: nuvemEnvioConfigs.nuvemEnvioCarrierId,
    });
    return () => {
      resetNuvemEnvioAsyncLabels();
      resetSearchNuvemEnvioLabels();
      resetDownloadNuvemEnvioLabel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      createNuvemEnvioAsyncLabelsStatus.isSuccess &&
      createNuvemEnvioAsyncLabelsData.labels.length
    ) {
      setShouldStartPolling(true);
    }
  }, [
    createNuvemEnvioAsyncLabelsStatus,
    createNuvemEnvioAsyncLabelsData,
    shouldStartPolling,
    setShouldStartPolling,
  ]);

  useEffect(() => {
    if (
      searchNuvemEnvioLabelsStatus.isSuccess ||
      searchNuvemEnvioLabelsStatus.isError
    ) {
      setShouldStartPolling(false);
    }
  }, [searchNuvemEnvioLabelsStatus, shouldStartPolling, setShouldStartPolling]);

  usePolling(
    shouldStartPolling,
    async () => {
      trackingNuvemEnvioPrintDocumentsGeneratingStartLabels(orderId);
      searchNuvemEnvioLabels({
        fulfillmentOrdersIds: createNuvemEnvioAsyncLabelsData.labels.map(
          (label) => label.fulfillmentOrderId,
        ),
      });

      return false;
    },
    {
      interval: NE_POOLING_LABELS_SEARCH_PROCESS_INTERVAL,
      timeout: NE_POOLING_LABELS_SEARCH_PROCESS_TIMEOUT,
      onTimeout: () => {
        trackingNuvemEnvioPrintDocumentsGeneratedErrorLabels(
          orderId,
          'Timeout',
        );
        setIsTimeout(true);
      },
    },
  );

  const hasError: boolean =
    createNuvemEnvioAsyncLabelsStatus.isError ||
    searchNuvemEnvioLabelsStatus.isError ||
    Boolean(createNuvemEnvioAsyncLabelsData?.errors?.length) ||
    Boolean(searchNuvemEnvioLabelsData?.erroredLabels?.length) ||
    isTimeout;

  const hasSuccess: boolean =
    searchNuvemEnvioLabelsStatus.isSuccess &&
    Boolean(searchNuvemEnvioLabelsData?.readyLabels?.length);

  const onProgress: boolean =
    !isTimeout &&
    (createNuvemEnvioAsyncLabelsStatus.isLoading ||
      searchNuvemEnvioLabelsStatus.isLoading);

  const combinedErrorCodes = useMemo(
    () =>
      combineLabelErrorCodes(
        createNuvemEnvioAsyncLabelsData?.errors,
        searchNuvemEnvioLabelsData?.erroredLabels,
        isTimeout,
      ),
    [
      createNuvemEnvioAsyncLabelsData?.errors,
      searchNuvemEnvioLabelsData?.erroredLabels,
      isTimeout,
    ],
  );

  useEffect(() => {
    if (hasSuccess) {
      trackingNuvemEnvioPrintDocumentsGeneratedPrintLabelsSuccess(orderId);
    }
  }, [hasSuccess, orderId]);

  useEffect(() => {
    if (hasError) {
      trackingNuvemEnvioPrintDocumentsGeneratedErrorLabels(
        orderId,
        combinedErrorCodes,
      );
    }
  }, [hasError, orderId, combinedErrorCodes]);

  return (
    <IonPageStratus
      width="small"
      headerTop={<HeaderTop navigation={{ onClick: goBack }} />}
    >
      <Page id="NuvemEnvioAsyncLabelGenerateStatusPage">
        <PrintLabelHeader information={information} />
        <Page.Body id="NuvemEnvioAsyncLabelGenerateStatusPageBody">
          {onProgress ? (
            <ProgressCard
              progressPercentage={
                searchNuvemEnvioLabelsData.progressInPercentage ?? 0
              }
            />
          ) : (
            <>
              {hasSuccess && (
                <Box display="flex" flexDirection="column" gap="4">
                  {isSafari && <OrdersSafariAlert />}
                  <PrintLabelResult
                    type="success"
                    labels={searchNuvemEnvioLabelsData.readyLabels}
                    orderId={orderId}
                  />
                </Box>
              )}
              {hasError && (
                <PrintLabelResult
                  type="error"
                  returnPath={getReturnPrintLabelUrl(source, orderId)}
                />
              )}
            </>
          )}
          {downloadNuvemEnvioLabelStatus.isSuccess &&
            !isMobile &&
            !!downloadNuvemEnvioLabelData.documentUrl && (
              <PdfPage url={downloadNuvemEnvioLabelData.documentUrl} />
            )}
        </Page.Body>
      </Page>
    </IonPageStratus>
  );
}

export default OrdersAsyncLabelGenerateStatusPage;
